import { computed, Injectable, signal } from '@angular/core';
import { Activity } from '@go-modules/interfaces/activity';
import { Task, TaskType } from '@go-modules/interfaces/task';
import { ActivityPreviewTaskPolicyService } from './activity-preview-task-policy.service';

@Injectable()
export class ActivityPreviewEditorStore {
	public readonly mode = signal<'create' | 'edit'>('edit');
	public readonly activity = signal<Activity | null>(null);
	public readonly originalActivity = signal<Activity | null>(null);
	public readonly tasks = signal<Task[]>([]);
	public readonly originalTasks = signal<Task[]>([]);
	public readonly selectedTaskType = signal<TaskType | null>(null);
	public readonly isLoading = signal(false);
	public readonly isSaving = signal(false);
	public readonly error = signal<unknown | null>(null);
	public readonly validationTarget = signal<TaskType | null>(null);

	public readonly previewTasks = computed(() =>
		this.taskPolicy.getPreviewTasks(this.tasks())
	);

	public readonly selectedTask = computed(() =>
		this.previewTasks().find((task) => task.type === this.selectedTaskType()) ?? null
	);

	public readonly isDirty = computed(() =>
		!activityEquivalent(this.activity(), this.originalActivity()) ||
		!tasksEquivalent(this.tasks(), this.originalTasks())
	);

	public readonly canSave = computed(() =>
		this.activity() != null &&
		this.previewTasks().length > 0 &&
		!this.isSaving()
	);

	constructor (private taskPolicy: ActivityPreviewTaskPolicyService) {}

	public initializeForCreate (
		activityDraft: Activity,
		tasks: Task[],
		initialTaskType: TaskType | null = null
	): void {
		this.initialize('create', activityDraft, tasks, initialTaskType);
	}

	public initializeForEdit (
		activity: Activity,
		tasks: Task[],
		initialTaskType: TaskType | null = null
	): void {
		this.initialize('edit', activity, tasks, initialTaskType);
	}

	public selectTask (type: TaskType): void {
		const activity = this.activity();
		if (activity == null || !this.taskPolicy.canAccessTaskType(type, activity, this.tasks())) {
			return;
		}

		this.selectedTaskType.set(type);
	}

	public resetSelectedTaskType (): void {
		this.selectedTaskType.set(null);
	}

	public updateActivity (activity: Activity): void {
		this.activity.set(structuredClone(activity));
		this.resolveSelectedTaskType();
	}

	public updateTask (task: Task): void {
		this.tasks.update((tasks) => {
			const updatedTasks = tasks.map((currentTask) =>
				this.taskMatches(currentTask, task) ? structuredClone(task) : currentTask
			);

			return this.withDerivedSortOrder(updatedTasks);
		});
		this.resolveSelectedTaskType();
	}

	public updateTasks (tasks: Task[]): void {
		this.tasks.set(this.withDerivedSortOrder(structuredClone(tasks)));
		this.resolveSelectedTaskType();
	}

	public addTask (task: Task): void {
		this.tasks.update((tasks) =>
			this.withDerivedSortOrder(
				[...tasks, structuredClone(task)]
					.sort((a, b) =>
						this.taskPolicy.getTaskTypeDefaultOrder(a.type) -
						this.taskPolicy.getTaskTypeDefaultOrder(b.type)
					)
			)
		);
		this.selectedTaskType.set(task.type);
		this.resolveSelectedTaskType();
	}

	public removeTask (type: TaskType): void {
		this.tasks.update((tasks) =>
			this.withDerivedSortOrder(tasks.map((task) =>
				task.type === type ? { ...task, deleted_at: task.deleted_at ?? new Date().toISOString() } : task
			))
		);
		this.resolveSelectedTaskType();
	}

	public markSaved (activity: Activity, tasks: Task[]): void {
		const activityClone = structuredClone(activity);
		const taskClones = structuredClone(tasks);
		this.activity.set(activityClone);
		this.originalActivity.set(structuredClone(activityClone));
		this.tasks.set(taskClones);
		this.originalTasks.set(structuredClone(taskClones));
		this.resolveSelectedTaskType();
	}

	public setValidationTarget (type: TaskType | null): void {
		this.validationTarget.set(type);
		if (type != null) {
			this.selectTask(type);
		}
	}

	private initialize (
		mode: 'create' | 'edit',
		activity: Activity,
		tasks: Task[],
		initialTaskType: TaskType | null
	): void {
		const activityClone = structuredClone(activity);
		const taskClones = structuredClone(tasks);

		this.mode.set(mode);
		this.activity.set(activityClone);
		this.originalActivity.set(structuredClone(activityClone));
		this.tasks.set(taskClones);
		this.originalTasks.set(structuredClone(taskClones));
		this.selectedTaskType.set(
			initialTaskType == null
				? null
				: this.taskPolicy.resolveInitialTaskType(initialTaskType, taskClones)
		);
		this.isLoading.set(false);
		this.isSaving.set(false);
		this.error.set(null);
		this.validationTarget.set(null);
		this.resolveSelectedTaskType();
	}

	private resolveSelectedTaskType (): void {
		const activity = this.activity();
		const selectedType = this.selectedTaskType();

		if (selectedType == null) {
			return;
		}

		if (
			activity != null &&
			this.taskPolicy.canAccessTaskType(selectedType, activity, this.tasks())
		) {
			return;
		}

		this.selectedTaskType.set(
			activity == null
				? null
				: this.taskPolicy.getDefaultPreviewTaskType(activity, this.tasks())
		);
	}

	private withDerivedSortOrder (tasks: Task[]): Task[] {
		return tasks.map((task, index) => ({ ...task, sort_order: index + 1 }));
	}

	private taskMatches (left: Task, right: Task): boolean {
		if (left.id !== 0 && right.id !== 0) {
			return left.id === right.id;
		}

		return left.type === right.type;
	}
}

export function activityEquivalent (left: Activity | null, right: Activity | null): boolean {
	return JSON.stringify(left) === JSON.stringify(right);
}

export function tasksEquivalent (left: Task[], right: Task[]): boolean {
	return JSON.stringify(normalizeTasks(left)) === JSON.stringify(normalizeTasks(right));
}

function normalizeTasks (tasks: Task[]): Task[] {
	return [...tasks].sort((a, b) => a.sort_order - b.sort_order);
}
